// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  State,
  Watch,
  Element,
  getAssetPath
} from "@stencil/core";
// Other Libraries
import { MercuryBundles } from "@genexus/mercury";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
// Custom Imports
import { Locale } from "../../common/locale";
import { mapItemsToComboBoxItemModel } from "./helpers";

const COPYRIGHTS_ILLUSTRATION = getAssetPath(
  `./gx-ide-assets/welcome-page/images/copyrights-illustration.png`
);
const ICON_NEXT = getAssetPath(
  `./gx-ide-assets/welcome-page/images/icon-next.svg`
);
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "components/button",
  "components/combo-box"
];

@Component({
  tag: "gx-ide-select-team-or-org",
  styleUrl: "select-team-or-org.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/select-team-or-org"]
})
export class GxIdeSelectTeamOrOrg {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #itemsModel: ComboBoxItemModel[] = [];
  #chSelectItemEl: HTMLChComboBoxRenderElement;
  #displayItemsTransitionTime: number = 100;

  @Element() el: HTMLGxIdeSelectTeamOrOrgElement;

  /**
   * Defines if the main slide is visible or not. Used for smooth transitions between slides.
   */
  @State() slideIsVisible: boolean = false;

  /**
   * Defines if the items model is ready to be rendered.
   */
  @State() itemsModelReady: boolean = false;

  /**
   * Callback executed when the user clicks the 'Cancel' button
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * Callback executed when the user clicks the 'Confirm' button
   */
  @Prop() readonly confirmCallback!: (itemId: string) => Promise<void>;

  /**
   * Whether the picker is selecting a team or an organization.
   * Drives the title, info message and combo label.
   */
  @Prop() readonly selectionType: SelectionType = "team";

  /**
   * Array that contains the selectable items (teams or organizations).
   */
  @Prop() readonly items!: SelectableItem[];
  @Watch("items")
  itemsChanged(newItems: SelectableItem[]) {
    this.slideIsVisible = false;

    if (!newItems?.length) {
      this.itemsModelReady = false;
      setTimeout(() => {
        this.slideIsVisible = true;
      }, this.#displayItemsTransitionTime);
      return;
    }

    // switch step
    setTimeout(() => {
      this.#itemsModel = mapItemsToComboBoxItemModel(newItems);
      this.itemsModelReady = true;
    }, this.#displayItemsTransitionTime);
    // show slide
    setTimeout(() => {
      this.slideIsVisible = true;
    }, this.#displayItemsTransitionTime * 2);
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    if (this.items?.length) {
      this.#itemsModel = mapItemsToComboBoxItemModel(this.items);
      this.itemsModelReady = true;
    }

    this.el.style.setProperty(
      "--display-items-transtion-time",
      this.#displayItemsTransitionTime.toString() + "ms"
    );
  }

  componentDidLoad() {
    this.slideIsVisible = true;
  }

  #continueHandler = async () => {
    await this.confirmCallback(this.#chSelectItemEl.value);
  };

  #cancelHandler = async () => {
    await this.cancelCallback();
  };

  render() {
    const kindLocale = this.#componentLocale[this.selectionType];
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="slide">
          <img
            src={COPYRIGHTS_ILLUSTRATION}
            class="slide__illustration"
            alt="" // decorative
          />

          <div
            class={{
              "slide__main": true,
              "slide__main--visible": this.slideIsVisible
            }}
          >
            <div class="slide__main-container">
              {this.itemsModelReady ? (
                [
                  <header class="slide__main-header">
                    <img
                      src={ICON_NEXT}
                      alt="" // decorative
                    />
                    <h2
                      // Split the title into two spans for better styling
                      class="slide__main-title"
                    >
                      <span>{kindLocale.title}</span>
                    </h2>
                  </header>,
                  <div class="slide__select-item-description">
                    <p class="body-semi-bold-l">{kindLocale.infoMessage}</p>
                  </div>,
                  <div class="field-group">
                    <div class="field field-block">
                      <label htmlFor="items">{kindLocale.label}</label>
                      <ch-combo-box-render
                        id="items"
                        accessibleName={kindLocale.label}
                        class="combo-box items-list"
                        model={this.#itemsModel}
                        value={this.#itemsModel && this.#itemsModel[0]?.value}
                        ref={(el: HTMLChComboBoxRenderElement) =>
                          (this.#chSelectItemEl =
                            el as HTMLChComboBoxRenderElement)
                        }
                      ></ch-combo-box-render>
                    </div>
                  </div>,
                  <div class="buttons-container">
                    <button
                      class="button-primary continue-btn"
                      onClick={this.#continueHandler}
                    >
                      {this.#componentLocale.confirmButtonCaption}
                    </button>
                    <button
                      class="button-secondary cancel-btn"
                      onClick={this.#cancelHandler}
                    >
                      {this.#componentLocale.cancelButtonCaption}
                    </button>
                  </div>
                ]
              ) : (
                <gx-ide-loader
                  show
                  loaderTitle={this.#componentLocale.loader.title}
                  description={this.#componentLocale.loader.description}
                ></gx-ide-loader>
              )}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}

export type SelectableItem = {
  id: string;
  name: string;
};

export type SelectionType = "team" | "organization";
